import NavigationBarHeading from '@/components/Common/DesignSystem/NavigationBar/Heading/NavigationBarHeading';
import NavigationBarIconButton from '@/components/Common/DesignSystem/NavigationBar/IconButton/NavigationBarIconButton';
import NavigationBarTextButton from '@/components/Common/DesignSystem/NavigationBar/TextButton/NavigationBarTextButton';
import NavigationBar from '@/components/Common/DesignSystem/NavigationBar/NavigationBar';
import SunnyLayout from '@/components/Common/SunnyLayout/SunnyLayout';
import SunnyNav from '@/components/Common/SunnyNav/SunnyNav';
import { SunnyFC } from '@/components/Common/SunnyNext';
import { withSunnyPage } from '@/components/Common/SunnyPage';
import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import { generatePageMeta } from '@/services/SEOService/metaGenerator';
import { Kinesis } from '@/services/TrackerService';
import EventCastStore, { EventCastStoreCtx } from '@/stores/EventCastStore';
import { forceBackWebView, isClientError } from '@/utils';
import { identity } from 'ramda';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import DisplayEventActions from '@/models/Display/Event/DisplayEventActions';
import DisplayEventRequestBuilder from '@/models/Display/Event/DisplayEventRequestBuilder';
import {
  BoardV2EventsResponseItemDto,
  IEventListPayloadItem,
} from '@/models/Display/Event/interfaces/IDisplayEventListPayload';
import ChipList, { IChipItem } from '@/components/Common/Widgets/Common/ChipList/ChipList';
import ToTopButton from '@/components/Common/ToTopButton/ToTopButton';
import { useScrollStore } from '@/utils/hooks';
import { EventTabId } from '@/models/Display/Event/interfaces/IDisplayEventListRequestParams';
import Popup from '@/components/Common/DesignSystem/Popup/Popup';
import Loader from '@/components/Common/SquareLoader/SquareLoader';
import { useRouter } from 'next/router';
import { useYaNative } from '@/utils/hooks/useYaNative';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { getYaNative } from '@yanolja-rnd/ya-native';

interface IEventProps {
  tabId: EventTabId;
  list: IEventListPayloadItem[];
  restoreData: boolean;
}

interface IEventQuery {
  [key: string]: unknown;
  /** 탭 아이디, eventTabQuery의 키값이 들어오길 기대함  */
  tab?: string;
}

const eventTabQuery: {
  promotion: Extract<EventTabId, 'promotionWideBannerSwipe'>;
  domestic: Extract<EventTabId, 'domesticTravel'>;
  overseas: Extract<EventTabId, 'overseasTravel'>;
  benefits: Extract<EventTabId, 'paymentBenefits'>;
} = {
  promotion: 'promotionWideBannerSwipe',
  domestic: 'domesticTravel',
  overseas: 'overseasTravel',
  benefits: 'paymentBenefits',
};
type ReverseMap<T extends Record<keyof T, keyof any>> = {
  [P in T[keyof T]]: {
    [K in keyof T]: T[K] extends P ? K : never;
  }[keyof T];
};
const tabIdToQuery: ReverseMap<typeof eventTabQuery> = {
  promotionWideBannerSwipe: 'promotion',
  domesticTravel: 'domestic',
  overseasTravel: 'overseas',
  paymentBenefits: 'benefits',
};

const tabPageName: Record<EventTabId, EN_PAGE_NAME> = {
  promotionWideBannerSwipe: EN_PAGE_NAME.PAGE__EVENT,
  domesticTravel: EN_PAGE_NAME.PAGE__EVENT__DOMESTIC_TAB,
  overseasTravel: EN_PAGE_NAME.PAGE__EVENT__OVERSEAS_TAB,
  paymentBenefits: EN_PAGE_NAME.PAGE__EVENT__BENEFITS_TAB,
};

const EventItem: React.FC<{ item: BoardV2EventsResponseItemDto }> = ({ item, item: { image } }) => {
  const yaNative = useYaNative();
  const [loading, setLoading] = useState(false);
  const linkProps = ({
    action: { app: deepLink, web },
  }: BoardV2EventsResponseItemDto): React.AnchorHTMLAttributes<HTMLAnchorElement> => {
    const onClick = (e: React.MouseEvent<HTMLElement>): void => {
      if (!(deepLink && yaNative?.isEnabled)) return;
      e.preventDefault();
      location.href = deepLink;
    };
    return { href: web, onClick, style: { aspectRatio: `${image.width}/${image.height}` } };
  };

  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!imgRef.current?.complete) setLoading(true);
  }, []);

  return (
    <a className={styles.eventItem} {...linkProps(item)}>
      <img
        ref={imgRef}
        onLoad={() => setLoading(false)}
        alt={item.title}
        src={item.image.url}
        className={clsx(loading && styles.eventImageLoading)}
        loading="lazy"
        width={item.image.width}
        height={item.image.height}
      />
    </a>
  );
};

const Event: SunnyFC<IEventProps, IEventQuery> = observer(({ list, restoreData = false, tabId }) => {
  const store = useRef(new EventCastStore({ list, restoreData, tabId })).current;
  const router = useRouter();
  const vertical = useMemo(() => list.some((x) => (x.image.height ?? 0) > (x.image.width ?? 0)), []);
  useScrollStore({ elRef: { current: null }, callback: store.onLoad });
  useEffect(() => {
    Kinesis.view('EventList', {
      objectType: 'deepLinkUri',
      object: location.href,
      referrer: document.referrer || undefined,
      eventTabName: tabIdToQuery[store.tabId],
      tabPosition: tabs.findIndex((x) => x.tabId === store.tabId) + 1,
    });
  }, []);

  const tabs: { title: string; tabId: EventTabId }[] = [
    { title: '추천', tabId: 'promotionWideBannerSwipe' },
    { title: '국내여행', tabId: 'domesticTravel' },
    { title: '해외여행', tabId: 'overseasTravel' },
    { title: '결제혜택', tabId: 'paymentBenefits' },
  ];
  const tabHref = (tabId: EventTabId): string => {
    const { route, query } = router;
    const searchParams = new URLSearchParams({ ...query, tab: tabIdToQuery[tabId] });
    const isFirstTab = tabs.findIndex((x) => x.tabId === tabId) === 0;
    if (isFirstTab) searchParams.delete('tab');
    return searchParams.toString() ? `${route}?${searchParams}` : route;
  };
  const items: IChipItem<'link'>[] = tabs.map(({ title, tabId }, index) => ({
    as: 'link',
    id: tabId,
    title,
    attributes: {
      href: tabHref(tabId),
      className: styles.tabChip,
      onClick: (e) => {
        e.preventDefault();
        const { href } = e.currentTarget;
        Kinesis.click('EventList', {
          objectType: 'deepLinkUri',
          object: href,
          eventTabName: tabIdToQuery[tabId],
          tabPosition: index + 1,
        });
        if (getYaNative().isEnabled && history.replaceState) {
          history.replaceState(null, document.title, href);
          history.go(0);
        } else {
          location.replace(href);
        }
      },
    },
  }));

  const yaNative = useYaNative();

  const openWvLink = (url: string) => (e: React.MouseEvent<HTMLElement>): void => {
    if (!yaNative?.isEnabled) return;
    e.preventDefault();
    yaNative.wv({ url });
  };

  const activeIndex = useCallback(() => tabs.findIndex((x) => x.tabId === store.tabId), [store.tabId]);

  return (
    <EventCastStoreCtx.Provider value={store}>
      <SunnyLayout className={styles.layout} bodyClassName={styles.main}>
        {{
          header: (
            <SunnyNav>
              <>
                <NavigationBar>
                  {{
                    left: (
                      <NavigationBarIconButton
                        as="button"
                        type="icon_arrowback"
                        onClick={() =>
                          forceBackWebView('/', undefined, ({ appEntryUrl }) => {
                            try {
                              if (!appEntryUrl) return false;
                              const url = new URL(appEntryUrl);
                              return url.origin + url.pathname === location.origin + location.pathname;
                            } catch (e) {
                              return false;
                            }
                          })
                        }
                      />
                    ),
                    center: <NavigationBarHeading>이벤트</NavigationBarHeading>,
                    right: (
                      <NavigationBarTextButton
                        className={styles.textButton}
                        {...(yaNative?.isEnabled
                          ? { as: 'button', onClick: openWvLink(location.origin + '/event/winner') }
                          : { as: 'link', href: '/event/winner' })}
                      >
                        당첨자 발표
                      </NavigationBarTextButton>
                    ),
                  }}
                </NavigationBar>
                <nav className={styles.tabs}>
                  <ChipList className={styles.tabsChipList} items={items} activeIndex={activeIndex()} />
                  <NavigationBar.Shadow className={styles.tabsShadow} />
                </nav>
              </>
            </SunnyNav>
          ),
          body: (
            <>
              {store.isLoading && <Loader />}
              <div className={styles.eventBody}>
                <div className={clsx(styles.itemsGrid, vertical && styles.vertical)}>
                  {store.list.map((x, i) => (
                    <EventItem key={`${x.title}-${i}`} item={x} />
                  ))}
                </div>
              </div>
              <Popup isOpen={store.isOpenErrorPopup} mainBtn={{ label: '확인', onClick: store.closeErrorPopup }}>
                {store.errorMessage}
              </Popup>
            </>
          ),
        }}
      </SunnyLayout>
      <ToTopButton />
    </EventCastStoreCtx.Provider>
  );
});

Event.getInitialProps = async ({ req, res, query }) => {
  /** Todo: [YAWEB-2061] getRedirectMyMenuLink 삭제 가능 */
  /** Todo: 향 후 SCS 로 이관 시 아래 코드가 유의미 함. 현재는 주석 처리
  const redirectURL = await getRedirectMyMenuLink('event', req);
  if (redirectURL) {
    return res.redirect(redirectURL);
  }
  */
  const tab = String(query.tab);
  const tabId: EventTabId = eventTabQuery[tab] || eventTabQuery.promotion;

  const actions = new DisplayEventActions(DisplayEventRequestBuilder, req);
  const entity = await actions.list({
    query: {
      limit: 20,
      page: 1,
      tabId,
    },
  });

  const list = entity
    .map((response) => response.data || [])
    .caseOf<void | IEventListPayloadItem[]>({
      left: (error) => {
        if (isClientError(error)) {
          return void res.status(404);
        }
        res.status(500);
      },
      right: identity,
    });

  if (!list) {
    return void res.status(500);
  }

  const referrer = req.session.getReferrer();
  return {
    tabId,
    list,
    restoreData: /\/event\/(\d.*)|winner/g.test(referrer),
    headProps: generatePageMeta({
      pageUrl: req.requestedUrl,
      pageName: tabPageName[tabId],
    }),
  };
};

export default withSunnyPage({
  smartBanner: () => {
    return {
      path: 'etc/event/list',
    };
  },
})(Event);
